import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import Logo from "@components/Logo/Logo";
import { BASE_URL } from "@config/apiConfig";
import { useNotification } from "@context/NotificationContext";
import { isGovernmentCompany } from "@features/licensing/schema/licenseRequestValidation";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  buildFormDraftFromBaseline,
  clearFormDraft,
  mergeFormDraft,
  readFormDraft,
  writeFormDraft,
} from "@hooks/formDraftPersistence";
import { useCompanyTypes } from "@hooks/useCompanyTypes";
import { SasTokenPrefix } from "@services/user/types";
import { trackScreenLoad } from "@telemetry/trackScreenLoad";
import { paths } from "@utils/navigation";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { VENDOR_LICENSE_REQUEST_FIELDS as F, tradeLicenseIssuingAuthorities } from "./constants";
import { useCreateVendorLicenseRequest } from "./hooks/useCreateVendorLicenseRequest";
import { useExternalDocumentUpload } from "./hooks/useExternalDocumentUpload";
import { useSyncCompanyType } from "./hooks/useSyncCompanyType";
import {
  getGovernmentVendorLicenseRequestFormSchema,
  getVendorLicenseRequestFormSchema,
  mapVendorLicenseRequestToPayload,
  vendorLicenseRequestDefaultValues,
  type VendorLicenseRequestFormData,
} from "./schema/vendorLicenseRequestFormSchema";
import { getCompanyTypeOptions } from "./utils";
import VendorLicenseRequestFormContent from "./VendorLicenseRequestFormContent";

const TEST_ID_BASE = "vendor-license-request";
const LANG_BASE = "licensing";
const DRAFT_STORAGE_KEY = "vendor-license-request-draft";
const DRAFT_IGNORED_FIELDS: string[] = [
  F.TRADE_LICENSE_DOCUMENT_URL,
  F.COMPANY_INCORPORATION_DOCUMENT_URL,
];
const DRAFT_PERSISTED_FIELDS = (Object.values(F) as string[]).filter(
  (field) => !DRAFT_IGNORED_FIELDS.includes(field),
);
const getUploadStoreKey = (field: string) => `vendor-license-request:${field}`;

const VendorLicenseRequestPage = () => {
  const { t, i18n } = useTranslation();
  const { notify } = useNotification();
  const startedAt = useRef(performance.now());
  const hasTrackedInitialLoad = useRef(false);
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const createRequestMutation = useCreateVendorLicenseRequest();
  const {
    data: companyTypes = [],
    isLoading: isCompanyTypesLoading,
    isError: isCompanyTypesError,
    error: companyTypesError,
  } = useCompanyTypes();
  const draftValues = useMemo(
    () => readFormDraft<VendorLicenseRequestFormData>(DRAFT_STORAGE_KEY),
    [],
  );
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isSubmitLocked, setIsSubmitLocked] = useState(false);
  const suspendDraftPersistenceRef = useRef(false);
  const hasInitialDraft = !!draftValues && Object.keys(draftValues).length > 0;

  const schema = useMemo(
    () => getVendorLicenseRequestFormSchema(t, companyTypes),
    [t, companyTypes],
  );
  const governmentSchema = useMemo(
    () => getGovernmentVendorLicenseRequestFormSchema(t, companyTypes),
    [t, companyTypes],
  );
  const initialValues = useMemo(
    () => mergeFormDraft(vendorLicenseRequestDefaultValues, draftValues),
    [draftValues],
  );
  const form = useForm<VendorLicenseRequestFormData>({
    resolver: (values, context, options) =>
      zodResolver(
        isGovernmentCompany(companyTypes, values.companyTypes) ? governmentSchema : schema,
      )(values, context, { ...options }),
    defaultValues: initialValues,
    mode: "onBlur",
  });
  const { watch } = form;

  const { isVendorDisabled } = useSyncCompanyType(
    companyTypes,
    form,
    isCompanyTypesLoading,
    isCompanyTypesError,
  );
  const companyTypeOptions = getCompanyTypeOptions(companyTypes, isVendorDisabled);

  const tradeLicenseIssuingAuthorityOptions = useMemo(
    () =>
      tradeLicenseIssuingAuthorities.map((authority) => ({
        value: authority.value,
        label: authority.label,
      })),
    [],
  );
  const fileUploadProfiles = useFileUploadProfiles();
  const documentConditions = useMemo(
    () => ({ ...fileUploadProfiles.certificates, hideErrors: false }),
    [fileUploadProfiles.certificates],
  );
  const supportedDocumentExtensions = useMemo(
    () => documentConditions.format.map((extension) => `.${extension.toLowerCase()}`),
    [documentConditions.format],
  );
  const uploadDescription = useMemo(
    () =>
      t("upload.additional-documents-upload", {
        extensions: supportedDocumentExtensions.join(", "),
        size: documentConditions.maxSizeLabel,
      }),
    [documentConditions.maxSizeLabel, supportedDocumentExtensions, t],
  );
  const tradeLicenseUpload = useExternalDocumentUpload(
    F.TRADE_LICENSE_DOCUMENT_URL,
    SasTokenPrefix.LICENSE_SERVICE,
    form,
    "TRADE_LICENSE",
    "VendorLicenseRequest",
    getUploadStoreKey(F.TRADE_LICENSE_DOCUMENT_URL),
  );
  const companyIncorpUpload = useExternalDocumentUpload(
    F.COMPANY_INCORPORATION_DOCUMENT_URL,
    SasTokenPrefix.LICENSE_SERVICE,
    form,
    "COMPANY_INCORPORATION",
    "VendorLicenseRequest",
    getUploadStoreKey(F.COMPANY_INCORPORATION_DOCUMENT_URL),
  );

  useEffect(() => {
    const subscription = watch((values) => {
      if (suspendDraftPersistenceRef.current) return;

      const draft = buildFormDraftFromBaseline(
        values,
        vendorLicenseRequestDefaultValues,
        DRAFT_PERSISTED_FIELDS,
        DRAFT_IGNORED_FIELDS,
      );

      if (Object.keys(draft).length > 0) {
        writeFormDraft(DRAFT_STORAGE_KEY, draft);
        return;
      }

      if (hasInitialDraft) {
        writeFormDraft(DRAFT_STORAGE_KEY, draftValues!);
        return;
      }

      clearFormDraft(DRAFT_STORAGE_KEY);
    });

    return () => subscription.unsubscribe();
  }, [draftValues, hasInitialDraft, watch]);

  useEffect(() => {
    if (
      isCompanyTypesLoading ||
      isCompanyTypesError ||
      companyTypesError ||
      hasTrackedInitialLoad.current
    ) {
      return;
    }

    hasTrackedInitialLoad.current = true;
    trackScreenLoad("Licenses", "MarHub Access Request", startedAt.current);
  }, [companyTypesError, isCompanyTypesError, isCompanyTypesLoading]);

  const onSubmit = async (values: VendorLicenseRequestFormData) => {
    if (isSubmitLocked) return;

    setIsSubmitLocked(true);
    const payload = mapVendorLicenseRequestToPayload(values, companyTypes);

    createRequestMutation.mutate(payload, {
      onSuccess: () => {
        suspendDraftPersistenceRef.current = true;
        clearFormDraft(DRAFT_STORAGE_KEY);
        tradeLicenseUpload.handleRemove();
        companyIncorpUpload.handleRemove();
        form.reset(vendorLicenseRequestDefaultValues);
      },
      onSettled: () => {
        setIsSubmitLocked(false);
      },
    });
  };

  const isUploading = tradeLicenseUpload.isPending || companyIncorpUpload.isPending;
  const isSubmitBusy =
    createRequestMutation.isPending || form.formState.isSubmitting || isSubmitLocked;

  const handleCancel = () => {
    if (isUploading) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }

    suspendDraftPersistenceRef.current = true;
    clearFormDraft(DRAFT_STORAGE_KEY);
    tradeLicenseUpload.handleRemove();
    companyIncorpUpload.handleRemove();
    form.reset(vendorLicenseRequestDefaultValues);
    window.location.assign(BASE_URL);
  };

  const cancelDialogProps = {
    testId: `${TEST_ID_BASE}-cancel-dialog`,
    title: t(`${LANG_BASE}.cancel-dialog-title`),
    cancelButtonText: t("common.no"),
    confirmButtonText: t("common.yes"),
    handleCloseDialog: () => setIsCancelDialogOpen(false),
    confirmAction: () => {
      setIsCancelDialogOpen(false);
      handleCancel();
    },
    elementContent: (
      <div className="text-left">
        <p>{t(`${LANG_BASE}.cancel-dialog-description`)}</p>
      </div>
    ),
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (isUploading) {
      event.preventDefault();
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }

    if (isSubmitBusy) {
      event.preventDefault();
      return;
    }

    void form.handleSubmit(onSubmit)(event);
  };

  return (
    <VendorLicenseRequestFormContent
      isTradeLicenseRequired={!isGovernmentCompany(companyTypes, watch(F.COMPANY_TYPES))}
      dir={dir}
      form={form}
      testIdBase={TEST_ID_BASE}
      texts={{
        title: t(`${LANG_BASE}.vendor-license-request-title`),
        requiredInformation: t(`${LANG_BASE}.vendor-license-request-required-information`),
        attachmentsIntro: t(`${LANG_BASE}.vendor-license-request-attachments-intro`),
        tradeLicenseDocTitle: t(`${LANG_BASE}.vendor-license-request-trade-license-doc-title`),
        companyIncorporationDocTitle: t(
          `${LANG_BASE}.vendor-license-request-company-incorporation-doc-title`,
        ),
        companyName: t(`${LANG_BASE}.vendor-license-request-company-name`),
        companyNameArabic: t(`${LANG_BASE}.vendor-license-request-company-name-arabic`),
        companyType: t(`${LANG_BASE}.vendor-license-request-company-type`),
        natureOfBusiness: t(`${LANG_BASE}.vendor-license-request-nature-of-business`),
        companyEmail: t(`${LANG_BASE}.vendor-license-request-company-email`),
        companyAddress: t(`${LANG_BASE}.vendor-license-request-company-address`),
        applicantName: t(`${LANG_BASE}.vendor-license-request-name-of-applicant`),
        applicantPosition: t(`${LANG_BASE}.vendor-license-request-applicant-position`),
        applicantEmail: t(`${LANG_BASE}.vendor-license-request-email-address`),
        telephoneNumber: t(`${LANG_BASE}.vendor-license-request-telephone-no`),
        mobileNumber: t(`${LANG_BASE}.vendor-license-request-mobile-number`),
        faxNumber: t(`${LANG_BASE}.vendor-license-request-fax-number`),
        tradingLicenseNumber: t(`${LANG_BASE}.vendor-license-request-trade-license-number`),
        tradingLicenseValidityDate: t(
          `${LANG_BASE}.vendor-license-request-trade-license-validity-date`,
        ),
        tradeLicenseIssuingAuthority: t(
          `${LANG_BASE}.vendor-license-request-trade-license-issuing-authority`,
        ),
        numberOfEmployees: t(`${LANG_BASE}.vendor-license-request-no-of-employees`),
        numberOfVehicles: t(`${LANG_BASE}.vendor-license-request-no-of-vehicles`),
        purposeOfRequest: t(`${LANG_BASE}.vendor-license-request-purpose-of-request`),
        cancel: t("common.cancel"),
        submit: t(`${LANG_BASE}.vendor-license-request-submit`),
        translate: t("common.translate"),
      }}
      companyTypeOptions={companyTypeOptions}
      tradeLicenseIssuingAuthorityOptions={tradeLicenseIssuingAuthorityOptions}
      supportedDocumentExtensions={supportedDocumentExtensions}
      documentConditions={documentConditions}
      uploadDescription={uploadDescription}
      tradeLicenseUpload={tradeLicenseUpload}
      companyIncorpUpload={companyIncorpUpload}
      submitDisabled={isSubmitBusy}
      onCancelClick={() => setIsCancelDialogOpen(true)}
      onFormSubmit={handleFormSubmit}
      cancelDialogOpen={isCancelDialogOpen}
      cancelDialogProps={cancelDialogProps}
      headerContent={
        <div className="px-24 h-16 flex items-center">
          <Logo height={40} to={paths.vendorLicenseRequest} />
        </div>
      }
    />
  );
};

export default VendorLicenseRequestPage;
